import {
  Bell,
  Check,
  Calendar,
  CalendarPlus,
  X,
  CheckCheck,
  Edit3,
  AlertCircle,
  MessageCircle,
  GraduationCap,
  type LucideIcon,
} from "lucide-react";
import { socialChannelStyle } from "@/lib/conversations";

/**
 * Forma mínima de una notificación que necesitan estas utilidades. Tanto
 * `NotificationsDropdown.tsx` (previsualización de la campanita) como
 * `pages/NotificationsHistory.tsx` (histórico completo paginado) declaran su
 * propio tipo local de notificación (con campos extra, p.ej. `data` tipado
 * como `Record<string, any>` en el dropdown para no romper el modal de
 * solicitud de cambio) y son estructuralmente compatibles con este tipo.
 */
export interface NotificationLike {
  type: string;
  data: Record<string, unknown> | null;
  title?: string;
}

interface NotificationTypeVisual {
  icon: LucideIcon;
  colorClass: string;
  label: string;
}

/**
 * Icono + color + etiqueta en español por tipo de notificación. Fuente de
 * verdad única compartida por la campanita y el histórico — si el backend
 * añade un tipo nuevo, añadirlo aquí (fallback abajo para tipos desconocidos).
 */
const NOTIFICATION_TYPE_CONFIG: Record<string, NotificationTypeVisual> = {
  new_reservation: {
    icon: Calendar,
    colorClass: "text-teal-500",
    label: "Nueva reserva",
  },
  reservation_cancelled: {
    icon: X,
    colorClass: "text-red-500",
    label: "Reserva cancelada",
  },
  reservation_confirmed: {
    icon: Check,
    colorClass: "text-green-500",
    label: "Reserva confirmada",
  },
  reservation_modified: {
    icon: Edit3,
    colorClass: "text-blue-500",
    label: "Reserva modificada",
  },
  reservation_updated: {
    icon: Edit3,
    colorClass: "text-blue-500",
    label: "Reserva actualizada",
  },
  reservation_reminder: {
    icon: Bell,
    colorClass: "text-amber-500",
    label: "Recordatorio de reserva",
  },
  reservation_change_request: {
    icon: AlertCircle,
    colorClass: "text-orange-500",
    label: "Solicitud de cambio",
  },
  reservation_change_approved: {
    icon: CheckCheck,
    colorClass: "text-green-500",
    label: "Cambio aprobado",
  },
  // CITAS (18-sep-2026). Mismo código de color que sus gemelos de mesas para
  // que el equipo los lea igual de rápido: verde-azulado para lo nuevo, rojo
  // para lo cancelado, azul para lo modificado.
  new_appointment: {
    icon: CalendarPlus,
    colorClass: "text-teal-500",
    label: "Nueva cita",
  },
  appointment_cancelled: {
    icon: X,
    colorClass: "text-red-500",
    label: "Cita cancelada",
  },
  appointment_modified: {
    icon: Edit3,
    colorClass: "text-blue-500",
    label: "Cita modificada",
  },
  appointment_change_request: {
    icon: AlertCircle,
    colorClass: "text-orange-500",
    label: "Cambio de cita solicitado",
  },
  appointment_change_approved: {
    icon: CheckCheck,
    colorClass: "text-green-500",
    label: "Cambio de cita aceptado",
  },
  appointment_change_rejected: {
    icon: X,
    colorClass: "text-red-500",
    label: "Cambio de cita rechazado",
  },
  reservation_change_rejected: {
    icon: X,
    colorClass: "text-red-500",
    label: "Cambio rechazado",
  },
  customer_created: {
    icon: Bell,
    colorClass: "text-purple-500",
    label: "Nuevo cliente",
  },
  // Módulo Clases: reserva de clase desde el widget (hoteles).
  new_class_booking: {
    icon: GraduationCap,
    colorClass: "text-indigo-500",
    label: "Nueva reserva de clase",
  },
  // Un cliente pide una persona en el chat. Verde de WhatsApp por defecto; si
  // llega por Instagram o Facebook, su color (ver `getNotificationTypeVisual`).
  whatsapp_handoff: {
    icon: MessageCircle,
    colorClass: "text-emerald-500",
    label: "Cliente pide ayuda en el chat",
  },
  system: {
    icon: Bell,
    colorClass: "text-gray-500",
    label: "Sistema",
  },
};

/** "reservation_change_request" -> "Reservation change request" (fallback para tipos sin etiqueta mapeada). */
function humanizeType(rawType: string): string {
  const normalized = rawType.replace(/[-_]/g, " ").trim();
  if (!normalized) return "Notificación";
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

/**
 * Canal de un aviso del chat ("instagram" / "messenger"), o null si es de
 * WhatsApp. Los avisos nuevos lo traen en `data.channel`; los anteriores al
 * 5-oct-2026 solo lo nombran en el título, y de ahí se saca.
 */
function chatChannelOf(notification?: Pick<NotificationLike, "data" | "title">): string | null {
  const channel = notification?.data?.channel;
  if (typeof channel === "string") return channel;
  const title = notification?.title ?? "";
  if (title.includes("Instagram")) return "instagram";
  if (title.includes("Facebook") || title.includes("Messenger")) return "messenger";
  return null;
}

/**
 * Devuelve icono + color + etiqueta de un tipo, con fallback para tipos que el
 * backend aún no tenga mapeados aquí. Con la notificación delante, los avisos
 * del chat salen con el color de su canal: así los de Instagram y Facebook no
 * se confunden con los de WhatsApp.
 */
export function getNotificationTypeVisual(
  type: string,
  notification?: Pick<NotificationLike, "data" | "title">,
): NotificationTypeVisual {
  const visual = NOTIFICATION_TYPE_CONFIG[type] || {
    icon: Bell,
    colorClass: "text-gray-500",
    label: humanizeType(type),
  };
  if (type !== "whatsapp_handoff") return visual;
  const channel = socialChannelStyle(chatChannelOf(notification));
  return channel ? { ...visual, colorClass: channel.icon } : visual;
}

/** Etiqueta en español de un tipo de notificación (para selects de filtro), con el mismo fallback que el resto de páginas del dashboard: clave cruda capitalizada. */
export function notificationTypeLabel(type: string): string {
  return getNotificationTypeVisual(type).label;
}

function getStringField(
  data: Record<string, unknown> | null | undefined,
  key: string,
): string | undefined {
  const value = data?.[key];
  return typeof value === "string" ? value : undefined;
}

/** Avisos de CITAS: abren la agenda en el día de la cita. */
const APPOINTMENT_LINK_TYPES = new Set([
  "new_appointment",
  "appointment_cancelled",
  "appointment_modified",
]);

const RESERVATION_LINK_TYPES = new Set([
  "new_reservation",
  "reservation_cancelled",
  "reservation_confirmed",
  "reservation_modified",
  "reservation_updated",
]);

/**
 * Enlace al que debe navegar una notificación al hacer clic, o `null` si no
 * tiene destino (p.ej. una solicitud de cambio, que en la campanita abre un
 * modal aparte en lugar de navegar).
 *
 * Si `data.reservationId` existe se añade `id=` al enlace: la página de
 * reservas abre el detalle de ESA reserva (aunque sea de otro día). La fecha
 * puede venir como `rawDate` (YYYY-MM-DD), como `date` legacy dd/MM/yyyy o —
 * el caso real más común en BD — como `date` en ISO completo
 * ("2026-08-31T00:00:00.000Z"), que antes no se reconocía y dejaba la
 * notificación sin enlace.
 */
export function getNotificationLink(notification: NotificationLike): string | null {
  // Los avisos del chat abren esa conversación en el inbox.
  if (notification.type === "whatsapp_handoff") {
    const conversationId = getStringField(notification.data, "conversationId");
    return conversationId ? `/conversations?open=${conversationId}` : "/conversations";
  }
  // Las citas viven en otra pantalla: mandarlas a /reservations dejaba al
  // equipo mirando una lista de mesas vacía (por eso el aviso de cita
  // modificada nacía sin enlace, disfrazado de "reservation_updated").
  if (APPOINTMENT_LINK_TYPES.has(notification.type)) {
    const params = new URLSearchParams();
    const rawDate = getStringField(notification.data, "rawDate");
    const appointmentId = getStringField(notification.data, "appointmentId");
    if (rawDate) params.set("date", rawDate.slice(0, 10));
    if (appointmentId) params.set("id", appointmentId);
    const query = params.toString();
    return query ? `/appointments?${query}` : "/appointments";
  }
  // Reservas de clase: abren la agenda de clases en su día.
  if (notification.type === "new_class_booking") {
    const rawDate = getStringField(notification.data, "rawDate");
    return rawDate ? `/classes?date=${rawDate.slice(0, 10)}` : "/classes";
  }
  if (!RESERVATION_LINK_TYPES.has(notification.type)) return null;

  const rawDate = getStringField(notification.data, "rawDate");
  const legacyDate = getStringField(notification.data, "date");

  let formattedDate = rawDate;
  if (!formattedDate && legacyDate) {
    if (legacyDate.includes("/")) {
      const parts = legacyDate.split("/");
      if (parts.length === 3) {
        formattedDate = `${parts[2]}-${parts[1].padStart(2, "0")}-${parts[0].padStart(2, "0")}`;
      }
    } else if (/^\d{4}-\d{2}-\d{2}/.test(legacyDate)) {
      // ISO ("2026-08-31" o "2026-08-31T00:00:00.000Z"): las reservas guardan
      // la fecha a medianoche UTC, así que los 10 primeros caracteres son el día.
      formattedDate = legacyDate.slice(0, 10);
    }
  }

  const reservationId = getStringField(notification.data, "reservationId");

  const params = new URLSearchParams();
  if (formattedDate) params.set("date", formattedDate);
  if (reservationId) params.set("id", reservationId);
  const query = params.toString();
  return query ? `/reservations?${query}` : null;
}

/**
 * Enlace a la reserva de una SOLICITUD DE CAMBIO (`reservation_change_request`).
 * Queda fuera de `getNotificationLink` a propósito: en la campanita ese tipo
 * abre el modal de aprobar/rechazar en vez de navegar, y este enlace es el
 * botón "Ver reserva" de ese modal (y la fila del historial). La fecha es la
 * de la reserva ACTUAL (`rawDate` en las nuevas, `currentDate` ISO en las
 * antiguas), no la solicitada.
 */
export function getChangeRequestReservationLink(
  data: Record<string, unknown> | null | undefined,
): string | null {
  const reservationId = getStringField(data, "reservationId");
  if (!reservationId) return null;
  const rawDate = getStringField(data, "rawDate");
  const currentDate = getStringField(data, "currentDate");
  const date =
    rawDate ??
    (currentDate && /^\d{4}-\d{2}-\d{2}/.test(currentDate)
      ? currentDate.slice(0, 10)
      : undefined);
  const params = new URLSearchParams();
  if (date) params.set("date", date);
  params.set("id", reservationId);
  return `/reservations?${params.toString()}`;
}

/**
 * Enlace a la cita de una SOLICITUD DE CAMBIO (`appointment_change_request`).
 * Abre la AGENDA en el día de la cita ACTUAL (no en el propuesto): es donde el
 * equipo la tiene delante para decidir.
 */
export function getChangeRequestAppointmentLink(
  data: Record<string, unknown> | null | undefined,
): string | null {
  const appointmentId = getStringField(data, "appointmentId");
  if (!appointmentId) return null;
  const rawDate = getStringField(data, "rawDate");
  const params = new URLSearchParams();
  if (rawDate) params.set("date", rawDate.slice(0, 10));
  params.set("id", appointmentId);
  return `/appointments?${params.toString()}`;
}
